import { useCallback } from 'react';

import { SequencerDevice } from '@repo/sdk/models/components';

import { AtlasRouteIds } from '@/components/navigation/constants';
import { NavItemConfig } from '@/components/navigation/types';
import { useGetSequencerDevices } from '@/hooks/query-hooks/gaia-api/agent-config';
import { useGetEquipmentBrowserConfig } from '@/hooks/query-hooks/gaia-api/equipment-browser';
import { useCurrentFacility } from '@/hooks/use-current-facility';

type SequencerNavConfig = {
  sequencers?: SequencerDevice[];
  searchParams?: { equipmentId: string };
  useSequencerNav: boolean;
};

type SystemViewNavConfig = {
  searchParams?: { viewId: string };
};

type NavConfig = {
  getSystemViewNavConfig: (item: NavItemConfig) => SystemViewNavConfig;
  getSequencerNavConfig: (item: NavItemConfig) => SequencerNavConfig;
};

const getNordicSequencerDevices = (item: NavItemConfig, sequencerDevices?: SequencerDevice[]) => {
  const nordicSequencerAlias: { [key: string]: string[] } = {
    [AtlasRouteIds.CompressorSequencer]: ['htr_1', 'ltr_1'],
    [AtlasRouteIds.CondenserSequencer]: ['hpr_1'],
  };

  return (
    sequencerDevices?.filter(
      (device) => device.kind === 'vessel' && nordicSequencerAlias[item.id].includes(device.alias)
    ) || []
  );
};

export const useNavConfig = (): NavConfig => {
  const { agentId, orgUrlName } = useCurrentFacility();
  // Non-Nordic branch uses BFF sequencer devices
  const { data: sequencerDevices } = useGetSequencerDevices();

  const { data: ebConfig } = useGetEquipmentBrowserConfig({ agentId });

  const getSystemViewNavConfig = useCallback(
    (item: NavItemConfig): SystemViewNavConfig => {
      if (item.id !== AtlasRouteIds.System) {
        return { searchParams: undefined };
      }
      if (!ebConfig) {
        return { searchParams: undefined };
      }
      // TODO: Add SystemViewID to API response once custom groups are added as names will be edittable.
      const systemView = ebConfig.views.find(
        (view) => view.name.toLowerCase() === AtlasRouteIds.System.toLowerCase()
      );
      if (!systemView || !systemView.id) {
        return { searchParams: undefined };
      }
      return {
        searchParams: { viewId: systemView.id },
      };
    },
    [ebConfig]
  );

  const getSequencerNavConfig = useCallback(
    (item: NavItemConfig) => {
      // Check to see if the item is a sequencer item
      if (
        item.id !== AtlasRouteIds.CompressorSequencer &&
        item.id !== AtlasRouteIds.CondenserSequencer
      ) {
        return {
          sequencers: undefined,
          searchParams: undefined,
          useSequencerNav: false,
        };
      }

      let sequencers: SequencerDevice[] = [];
      let equipmentId: string | undefined;

      if (orgUrlName === 'nordic') {
        // Nordic uses ControlledDevice with alias for vessel filtering
        const nordicSequencers = getNordicSequencerDevices(item, sequencerDevices);
        sequencers = nordicSequencers;
        equipmentId = nordicSequencers[0]?.id;
      } else {
        // Non-Nordic uses BFF SequencerDevice (already filtered by kind on server)
        const deviceKind =
          item.id === AtlasRouteIds.CompressorSequencer
            ? 'compressor sequencer'
            : 'condenser sequencer';
        const bffSequencers =
          sequencerDevices?.filter((device) => device.kind === deviceKind) || [];
        sequencers = bffSequencers;
        equipmentId = bffSequencers[0]?.id;
      }

      // if there are no sequencers, return false
      if (sequencers.length === 0) {
        return {
          sequencers: undefined,
          searchParams: undefined,
          useSequencerNav: false,
        };
      }
      // if there are multiple sequencers use the sequencer nav
      const useSequencerNav = sequencers.length > 1;

      return {
        sequencers,
        searchParams: equipmentId ? { equipmentId } : undefined,
        useSequencerNav,
      };
    },
    [orgUrlName, sequencerDevices]
  );

  return {
    getSequencerNavConfig,
    getSystemViewNavConfig,
  };
};
